import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import PlatformIcon, { platformLabel } from "./PlatformIcon";
import { SeverityPill, timeAgo } from "./bits";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { ExternalLink, Heart, MessageCircle, Repeat2 } from "lucide-react";

export default function LiveTicker({ onPostClick }) {
  const [items, setItems] = useState([]);
  const trackRef = useRef(null);
  const [paused, setPaused] = useState(false);
  const offsetRef = useRef(0);
  const rafRef = useRef(0);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const r = await api.get("/posts/live?limit=30");
        if (mounted) setItems(r.data.items);
      } catch (e) {}
    };
    load();
    const int = setInterval(load, 30000);
    return () => { mounted = false; clearInterval(int); };
  }, []);

  // Continuous marquee (RAF) - pauses on hover
  useEffect(() => {
    const el = trackRef.current;
    if (!el || items.length === 0) return;
    const step = () => {
      if (!paused) {
        offsetRef.current -= 0.6;
        const width = el.scrollWidth / 2;
        if (Math.abs(offsetRef.current) >= width) offsetRef.current = 0;
        el.style.transform = `translateX(${offsetRef.current}px)`;
      }
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [items, paused]);

  if (items.length === 0) {
    return (
      <div className="cimap-ticker h-9 flex items-center px-4 text-[11px] uppercase tracking-[0.3em] text-white/50">
        Live feed initializing…
      </div>
    );
  }
  // duplicate for seamless loop
  const loop = [...items, ...items];

  return (
    <div className="cimap-ticker relative overflow-hidden" data-testid="live-ticker">
      <div className="flex items-center">
        <div className="flex items-center gap-2 px-4 h-9 border-r border-white/10 bg-[#020617] shrink-0">
          <div className="pulse-dot" />
          <span className="text-[10px] uppercase tracking-[0.3em] text-white/80 font-semibold">Live · Social Intel</span>
        </div>
        <div
          className="flex-1 overflow-hidden relative"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          <div ref={trackRef} className="flex whitespace-nowrap will-change-transform" style={{ transform: "translateX(0)" }}>
            {loop.map((p, i) => (
              <HoverCard key={`${p.id}-${i}`} openDelay={80} closeDelay={80}>
                <HoverCardTrigger asChild>
                  <button
                    className="cimap-ticker-chip"
                    onClick={() => onPostClick && onPostClick(p)}
                    data-testid={`ticker-chip-${i}`}
                  >
                    <PlatformIcon platform={p.platform} className="h-3.5 w-3.5" />
                    <span className="text-white/55 text-[11px] font-mono">{timeAgo(p.created_at)}</span>
                    <span className="text-white/90">{p.author}:</span>
                    <span className="text-white/70 max-w-[420px] overflow-hidden text-ellipsis">
                      {p.content.slice(0, 110)}{p.content.length > 110 ? "…" : ""}
                    </span>
                    <SeverityPill severity={p.severity} />
                  </button>
                </HoverCardTrigger>
                <HoverCardContent side="bottom" align="start" className="w-[420px] p-0 rounded-sm border-slate-200">
                  <PostPreview post={p} />
                </HoverCardContent>
              </HoverCard>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function PostPreview({ post }) {
  return (
    <div className="bg-white">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <PlatformIcon platform={post.platform} className="h-4 w-4" />
          <span className="text-xs font-semibold">{post.author}</span>
          <span className="text-xs text-slate-400 font-mono">{post.handle}</span>
        </div>
        <SeverityPill severity={post.severity} />
      </div>
      <div className="px-4 py-3">
        <p className="text-sm text-slate-700 leading-relaxed">{post.content}</p>
        {post.image && (
          <img src={post.image} alt="" className="mt-3 w-full h-36 object-cover rounded-sm" />
        )}
      </div>
      <div className="flex items-center justify-between px-4 py-2 border-t border-slate-100 text-xs text-slate-500 font-mono">
        <div className="flex items-center gap-4">
          <span className="flex items-center gap-1"><Heart className="h-3 w-3" /> {post.likes}</span>
          <span className="flex items-center gap-1"><Repeat2 className="h-3 w-3" /> {post.shares}</span>
          <span className="flex items-center gap-1"><MessageCircle className="h-3 w-3" /> {post.comments}</span>
        </div>
        <div className="flex items-center gap-2">
          <span>{platformLabel(post.platform)}</span>
          <ExternalLink className="h-3 w-3" />
        </div>
      </div>
    </div>
  );
}
